import { statusLabel, THEMEPARKS_WIKI_SOURCE, type WikiShowtime } from "../../config/themeparksWiki";
import type { UseParkWaitTimesResult } from "../../hooks/useParkWaitTimes";
import "./themeparksWiki.css";

interface LiveWaitChipProps {
  waitMinutes: number | null;
  status: string | null;
  showtimes?: WikiShowtime[];
  stale: boolean;
  fetchedAt: number | null;
  loading: boolean;
}

function formatRelative(ms: number | null): string | null {
  if (ms == null) return null;
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/**
 * A showtime on the park's clock. The feed stamps each one with the park's
 * UTC offset ("2026-10-05T19:00:00-07:00"), so the wall time it names is the
 * park's; it's shown as written, marked "park time" when this phone's clock
 * disagrees — a 7:00 PM show read from Boston is still 7:00 PM, not 10:00.
 */
function formatShowtime(t: WikiShowtime): string | null {
  if (!t.startTime) return null;
  const d = new Date(t.startTime);
  if (Number.isNaN(d.getTime())) return null;
  const m = /T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/.exec(t.startTime);
  if (!m) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const [, hh, mm, off] = m as unknown as [string, string, string, string];
  const wall = new Date(2000, 0, 1, Number(hh), Number(mm));
  const text = wall.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const offsetMin =
    off === "Z" ? 0 : (off.startsWith("-") ? -1 : 1) * (Number(off.slice(1, 3)) * 60 + Number(off.slice(-2)));
  // getTimezoneOffset is minutes *behind* UTC, so a match is its negation.
  return offsetMin === -d.getTimezoneOffset() ? text : `${text} park time`;
}

/** Compact live-status badge for a wiki-linked ATTRACTION / SHOW. */
export function LiveWaitChip({
  waitMinutes,
  status,
  showtimes,
  stale,
  fetchedAt,
  loading,
}: LiveWaitChipProps) {
  if (loading) {
    return <span className="live-wait-chip live-wait-chip-loading">Live data…</span>;
  }

  const upcomingShowtimes = (showtimes ?? [])
    .map(formatShowtime)
    .filter((s): s is string => !!s)
    .slice(0, 4);

  let main: string;
  let tone: "ok" | "warn" | "muted";
  if (typeof waitMinutes === "number" && (status == null || status === "OPERATING" || status === "UNKNOWN")) {
    main = `${waitMinutes} min`;
    tone = waitMinutes >= 60 ? "warn" : "ok";
  } else if (status === "DOWN") {
    main = "Down";
    tone = "warn";
  } else if (status === "CLOSED") {
    main = "Closed";
    tone = "muted";
  } else if (status === "REFURBISHMENT") {
    main = "Refurb";
    tone = "muted";
  } else if (status === "OPERATING") {
    main = upcomingShowtimes.length > 0 ? "Showtimes" : "Operating";
    tone = "ok";
  } else if (status && status !== "UNKNOWN") {
    main = statusLabel(status) ?? status;
    tone = "muted";
  } else if (fetchedAt == null) {
    // No cache entry at all for this entity yet.
    return null;
  } else {
    main = "No wait posted";
    tone = "muted";
  }

  const rel = formatRelative(fetchedAt);
  return (
    <span className={`live-wait-chip live-wait-chip-${tone}${stale ? " live-wait-chip-stale" : ""}`}>
      <span className="live-wait-dot" aria-hidden="true" />
      <span className="live-wait-main">{main}</span>
      {upcomingShowtimes.length > 0 && (
        <span className="live-wait-showtimes">{upcomingShowtimes.join(" · ")}</span>
      )}
      {rel && (
        <span className="live-wait-meta">
          {stale ? "stale · " : ""}
          {rel}
        </span>
      )}
    </span>
  );
}

interface ChildWaitBadgeProps {
  child: { externalSource?: string | null; externalId?: string | null };
  parkWaitTimes: UseParkWaitTimesResult;
}

/** Live wait chip for a park's child entity, when it's wiki-linked and cached. */
export function ChildWaitBadge({ child, parkWaitTimes }: ChildWaitBadgeProps) {
  if (child.externalSource !== THEMEPARKS_WIKI_SOURCE || !child.externalId) return null;
  const snap = parkWaitTimes.entities[child.externalId];
  if (!snap) return null;
  return (
    <span className="entity-card-wait">
      <LiveWaitChip
        waitMinutes={typeof snap.waitMinutes === "number" ? snap.waitMinutes : null}
        status={snap.status ?? null}
        showtimes={snap.showtimes}
        stale={parkWaitTimes.stale}
        fetchedAt={parkWaitTimes.fetchedAt}
        loading={false}
      />
    </span>
  );
}
